import React, { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import { EmojiEvents } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../auth/authSlice";
import {
  useGetWeeklyLeaderboardQuery,
  useGetClassroomQuizLeaderboardQuery,
  useGetClassroomAcademicRankingQuery,
} from "../../../../../app/leaderboardApiSlice";
import { useGetMyClassesQuery } from "../../../../../app/questionApiSlice";
import UserAvatar from "../../../../common/UserAvatar";

const MEDAL_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];

const getDisplayName = (user) => {
  const p = user?.auth_profile;
  if (p?.first_name || p?.last_name)
    return `${p.first_name || ""} ${p.last_name || ""}`.trim();
  return user?.username || user?.schoola_id || "—";
};

const getInitials = (user) => {
  const name = getDisplayName(user);
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
};

function RankCell({ rank }) {
  if (rank <= 3) {
    return (
      <EmojiEvents
        sx={{ color: MEDAL_COLORS[rank - 1], fontSize: "1.4rem", verticalAlign: "middle" }}
      />
    );
  }
  return (
    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
      #{rank}
    </Typography>
  );
}

function LoadingRows({ count = 8 }) {
  return (
    <Box>
      {[...Array(count)].map((_, i) => (
        <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
      ))}
    </Box>
  );
}

function EmptyState({ message }) {
  return (
    <Box sx={{ textAlign: "center", py: 6 }}>
      <EmojiEvents sx={{ fontSize: "4rem", color: "#e0e0e0", mb: 1 }} />
      <Typography color="text.secondary">{message}</Typography>
    </Box>
  );
}

// ── Quiz Rankings Tab ─────────────────────────────────────────────────────────

function QuizRankingsTab({ classrooms }) {
  const authData = useSelector((state) => state.auth);
  const userId = authData?.user?.id;
  const [classroomId, setClassroomId] = useState("");
  const [cursor, setCursor] = useState(null);
  const [allEntries, setAllEntries] = useState([]);
  const sentinelRef = React.useRef(null);

  const {
    data: schoolWideData,
    isLoading: loadingSchoolWide,
    isFetching: fetchingSchoolWide,
  } = useGetWeeklyLeaderboardQuery(cursor ?? {}, { skip: !!classroomId });

  const {
    data: classroomData,
    isLoading: loadingClassroom,
  } = useGetClassroomQuizLeaderboardQuery(classroomId, { skip: !classroomId });

  // Reset accumulated entries when switching to school-wide
  React.useEffect(() => {
    if (!classroomId) {
      setCursor(null);
      setAllEntries([]);
    }
  }, [classroomId]);

  // Accumulate pages for school-wide infinite scroll
  React.useEffect(() => {
    if (classroomId || !schoolWideData?.items || fetchingSchoolWide) return;
    setAllEntries((prev) =>
      cursor === null ? schoolWideData.items : [...prev, ...schoolWideData.items]
    );
  }, [schoolWideData, cursor, classroomId, fetchingSchoolWide]);

  // IntersectionObserver sentinel to load next page
  React.useEffect(() => {
    if (classroomId) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && schoolWideData?.has_next && !fetchingSchoolWide) {
          setCursor({
            after_points: schoolWideData.next_after_points,
            after_id: schoolWideData.next_after_id,
          });
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [classroomId, schoolWideData?.has_next, schoolWideData?.next_after_points, schoolWideData?.next_after_id, fetchingSchoolWide]);

  const classroomEntries = React.useMemo(() => {
    if (!classroomData) return [];
    const list = Array.isArray(classroomData) ? classroomData : [];
    return [...list].sort((a, b) => (b.points || 0) - (a.points || 0));
  }, [classroomData]);

  const entries = classroomId ? classroomEntries : allEntries;
  const isInitialLoading = classroomId ? loadingClassroom : (loadingSchoolWide && cursor === null);
  const isFetchingMore = !classroomId && fetchingSchoolWide && cursor !== null;
  const hasMore = !classroomId && !!schoolWideData?.has_next;

  const weekLabel = allEntries[0]?.name || "";

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Filter by classroom</InputLabel>
          <Select
            value={classroomId}
            label="Filter by classroom"
            onChange={(e) => setClassroomId(e.target.value)}
          >
            <MenuItem value="">
              <em>All classrooms (school-wide)</em>
            </MenuItem>
            {(classrooms || []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {weekLabel && (
          <Typography variant="caption" color="text.secondary">
            Week {weekLabel}
          </Typography>
        )}
      </Box>

      {isInitialLoading ? (
        <LoadingRows />
      ) : entries.length === 0 ? (
        <EmptyState message="No quiz entries yet this week." />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "#2a2a2a" : "#FAFAFA",
                }}
              >
                <TableCell sx={{ fontWeight: 700, width: 60 }}>Rank</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Points
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Questions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry, idx) => {
                const rank = idx + 1;
                const isMe = entry.user_id === userId;
                return (
                  <TableRow
                    key={entry.id}
                    sx={{
                      bgcolor: isMe
                        ? (theme) =>
                            theme.palette.mode === "dark" ? "#2e2a1a" : "#FFF8E1"
                        : "inherit",
                      "&:hover": {
                        bgcolor: isMe
                          ? (theme) =>
                              theme.palette.mode === "dark" ? "#3a3010" : "#FFF3CD"
                          : (theme) =>
                              theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                      },
                    }}
                  >
                    <TableCell>
                      <RankCell rank={rank} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <UserAvatar
                          src={entry.user?.auth_profile?.photo || undefined}
                          name={getDisplayName(entry.user)}
                          sx={{
                            width: 34,
                            height: 34,
                            fontSize: "0.85rem",
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: isMe ? 700 : 500 }}>
                          {getDisplayName(entry.user)}
                          {isMe && (
                            <Chip
                              label="You"
                              size="small"
                              sx={{
                                ml: 1,
                                height: 18,
                                fontSize: "0.65rem",
                                bgcolor: "#F59E0B",
                                color: "#fff",
                              }}
                            />
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {(entry.points || 0).toFixed(1)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {entry.questions || 0}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Infinite scroll sentinel — only for school-wide view */}
      {!classroomId && (
        <Box ref={sentinelRef} sx={{ display: "flex", justifyContent: "center", py: 2, minHeight: 40 }}>
          {isFetchingMore && <CircularProgress size={24} />}
          {!hasMore && entries.length > 0 && (
            <Typography variant="caption" color="text.disabled">
              All {entries.length} entries loaded
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Academic Rankings Tab ─────────────────────────────────────────────────────

function AcademicRankingsTab({ classrooms }) {
  const [classroomId, setClassroomId] = useState("");

  const { data, isLoading } = useGetClassroomAcademicRankingQuery(
    { classroomId },
    { skip: !classroomId }
  );

  const ranking = data?.ranking || [];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Select classroom</InputLabel>
          <Select
            value={classroomId}
            label="Select classroom"
            onChange={(e) => setClassroomId(e.target.value)}
          >
            <MenuItem value="" disabled>
              <em>Select a classroom</em>
            </MenuItem>
            {(classrooms || []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!classroomId ? (
        <EmptyState message="Select a classroom to view academic rankings." />
      ) : isLoading ? (
        <LoadingRows />
      ) : ranking.length === 0 ? (
        <EmptyState message="No scores recorded for this classroom yet." />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "#2a2a2a" : "#FAFAFA",
                }}
              >
                <TableCell sx={{ fontWeight: 700, width: 60 }}>Position</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Total Score
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ranking.map((row) => (
                <TableRow
                  key={row.student_id}
                  sx={{
                    "&:hover": {
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                    },
                  }}
                >
                  <TableCell>
                    <RankCell rank={row.position} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.student_name}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {row.total_score}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const TeacherLeaderboard = () => {
  const [tab, setTab] = useState(0);
  const { data: classrooms, isLoading: loadingClassrooms } = useGetMyClassesQuery();

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
        <EmojiEvents sx={{ color: "#F59E0B", fontSize: "2rem" }} />
        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          Leaderboard
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Quiz Rankings" />
        <Tab label="Academic Rankings" />
      </Tabs>

      {loadingClassrooms ? (
        <LoadingRows count={3} />
      ) : (
        <>
          {tab === 0 && <QuizRankingsTab classrooms={classrooms} />}
          {tab === 1 && <AcademicRankingsTab classrooms={classrooms} />}
        </>
      )}
    </Box>
  );
};

export default TeacherLeaderboard;
